import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Target, Activity, Globe } from 'lucide-react';
export const About = () => {
  return <div className="min-h-screen pt-32 pb-24" data-id="element-103">
      <div className="max-w-4xl mx-auto px-6 md:px-12" data-id="element-104">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center mb-20" data-id="element-105">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-panel border-white/10 mb-6" data-id="element-106">
            <span className="text-xs font-mono text-lab-muted tracking-wider uppercase" data-id="element-107">
              Manifiesto
            </span>
          </div>
          <h1 className="text-4xl md:text-6xl font-display font-bold mb-6" data-id="element-108">
            La Tesis del <span className="text-gradient" data-id="element-109">Laboratorio</span>
          </h1>
          <p className="text-xl text-lab-muted font-light leading-relaxed" data-id="element-110">
            CLVN–LRR no es una tienda. Es una instalación de investigación
            digital enfocada en la optimización del rendimiento cognitivo humano
            a través de sistemas y tecnología.
          </p>
        </motion.div>

        <div className="space-y-24" data-id="element-111">
          {/* Section 1 */}
          <motion.section initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="grid md:grid-cols-2 gap-12 items-center" data-id="element-112">
            <div data-id="element-113">
              <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center mb-6 border border-white/10" data-id="element-114">
                <Target className="text-lab-neon" size={24} data-id="element-115" />
              </div>
              <h2 className="text-3xl font-display font-bold mb-4" data-id="element-116">
                El Problema de la Fricción
              </h2>
              <p className="text-lab-muted leading-relaxed" data-id="element-117">
                La mayoría de los profesionales operan con "sistemas operativos
                mentales" obsoletos. Gastan el 80% de su energía cognitiva
                recordando tareas, buscando información y gestionando el caos,
                en lugar de realizar trabajo profundo y estratégico.
              </p>
            </div>
            <div className="aspect-square glass-card rounded-3xl flex items-center justify-center relative overflow-hidden" data-id="element-118">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiLz48L3N2Zz4=')] opacity-50" data-id="element-119" />
              <div className="w-32 h-32 border border-red-500/30 rounded-full flex items-center justify-center relative" data-id="element-120">
                <div className="absolute w-full h-[1px] bg-red-500/30 rotate-45" data-id="element-121" />
                <div className="absolute w-full h-[1px] bg-red-500/30 -rotate-45" data-id="element-122" />
              </div>
            </div>
          </motion.section>

          {/* Section 2 */}
          <motion.section initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="grid md:grid-cols-2 gap-12 items-center md:flex-row-reverse" data-id="element-123">
            <div className="order-1 md:order-2" data-id="element-124">
              <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center mb-6 border border-white/10" data-id="element-125">
                <Activity className="text-lab-blueLight" size={24} data-id="element-126" />
              </div>
              <h2 className="text-3xl font-display font-bold mb-4" data-id="element-127">
                Nuestra Solución
              </h2>
              <p className="text-lab-muted leading-relaxed" data-id="element-128">
                Diseñamos arquitecturas digitales elegantes que actúan como
                exoesqueletos para tu mente. Externalizamos la memoria y la
                gestión para que tu cerebro pueda hacer lo que mejor hace:
                conectar ideas y resolver problemas complejos.
              </p>
            </div>
            <div className="order-2 md:order-1 aspect-square glass-card rounded-3xl flex items-center justify-center relative overflow-hidden" data-id="element-129">
              <div className="absolute inset-0 bg-gradient-to-br from-lab-blue/10 to-transparent" data-id="element-130" />
              <div className="w-32 h-32 border border-lab-blueLight/50 rounded-full flex items-center justify-center relative animate-[pulse_3s_ease-in-out_infinite]" data-id="element-131">
                <div className="w-16 h-16 bg-lab-blueLight/20 rounded-full blur-md" data-id="element-132" />
              </div>
            </div>
          </motion.section>

          {/* Core Values */}
          <motion.section initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="border-t border-white/10 pt-16" data-id="element-133">
            <h2 className="text-2xl font-display font-bold mb-8 text-center" data-id="element-134">
              Principios de Diseño
            </h2>
            <div className="grid sm:grid-cols-3 gap-6" data-id="element-135">
              {[{
              title: 'Minimalismo Radical',
              desc: 'Eliminamos todo lo que no contribuye directamente al rendimiento.'
            }, {
              title: 'Estética Funcional',
              desc: 'La belleza no es un adorno, es una herramienta para reducir la carga cognitiva.'
            }, {
              title: 'Escalabilidad',
              desc: 'Sistemas diseñados para crecer contigo, desde el día 1 hasta el IPO.'
            }].map((val, i) => <div key={i} className="glass-panel p-6 rounded-2xl" data-id="element-136">
                  <h3 className="font-display font-semibold text-white mb-2" data-id="element-137">
                    {val.title}
                  </h3>
                  <p className="text-sm text-lab-muted" data-id="element-138">{val.desc}</p>
                </div>)}
            </div>
          </motion.section>
        </div>
      </div>
    </div>;
};